"use client"
import type React from "react"
import { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Spinner,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Table,
} from "reactstrap"
import {
  FaArrowLeft,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaCamera,
  FaExclamationTriangle,
  FaEdit,
  FaTimesCircle,
} from "react-icons/fa"
import { BadgeRequestService } from "../../../Service/BadgeRequestService"
import type { Participation, Representation, ExhibitionPricing, PrintOrder } from "./badge-request"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  participation: Participation
  onBack: () => void
  onOrderCreated: (order: PrintOrder) => void
}

interface PhotoValidationError {
  error: string
  message: string
  representatives_without_photos: Array<{
    id: number
    name: string
    email: string
    phone: string
  }>
  photo_required: boolean
}

const RepresentationsList: React.FC<Props> = ({ participation, onBack, onOrderCreated }) => {
  const [representations, setRepresentations] = useState<Representation[]>([])
  const [selectedRepresentations, setSelectedRepresentations] = useState<number[]>([])
  const [pricing, setPricing] = useState<ExhibitionPricing | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Photo validation states
  const [photoValidationError, setPhotoValidationError] = useState<PhotoValidationError | null>(null)
  const [showPhotoModal, setShowPhotoModal] = useState(false)

  useEffect(() => {
    fetchData()
  }, [participation.id])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [repsData, pricingData] = await Promise.all([
        BadgeRequestService.getParticipationRepresentations(participation.id),
        BadgeRequestService.getExhibitionPricing(participation.exhibition),
      ])
      setRepresentations(repsData)
      setPricing(pricingData)
    } catch (error) {
      console.error("Failed to fetch data:", error)
      toast.error("Failed to load data")
    } finally {
      setLoading(false)
    }
  }

  const handleRepresentationToggle = (representationId: number) => {
    setSelectedRepresentations((prev) =>
      prev.includes(representationId) ? prev.filter((id) => id !== representationId) : [...prev, representationId],
    )
  }

  const calculateTotalAmount = () => {
    if (!pricing) return 0
    const exceedsLimitCount = selectedRepresentations.filter((id) => {
      const rep = representations.find((r) => r.id === id)
      return rep?.exceeds_limit
    }).length
    return exceedsLimitCount * pricing.badge_fee
  }

  const handleEditRepresentative = (representativeId: number) => {
    // Navigate to edit representative page or open edit modal
    console.log("Edit representative:", representativeId)
    toast.info("Please navigate to Staff Management to upload photos")
    // You can implement navigation logic here
    // For example: router.push(`/staff/edit/${representativeId}`)
  }

  const handleSubmitOrder = async () => {
    if (selectedRepresentations.length === 0) {
      toast.error("Please select at least one representative")
      return
    }

    const totalAmount = calculateTotalAmount()
    const exceedsCount = selectedRepresentations.filter((id) => {
      const rep = representations.find((r) => r.id === id)
      return rep?.exceeds_limit
    }).length

    let confirmMessage = `You are requesting badges for ${selectedRepresentations.length} participant(s).`
    if (exceedsCount > 0) {
      confirmMessage += `\n\n${exceedsCount} Participant(s) exceed the free limit and will require payment of ${pricing?.currency} ${totalAmount.toLocaleString()}.`
      confirmMessage += `\n\nYou can submit the request now and make payment later from your orders list.`
    } else {
      confirmMessage += "\n\nAll selected participant are within the free limit - no payment required."
    }

    const result = await Swal.fire({
      title: "Confirm Badge Request",
      text: confirmMessage,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Submit Request",
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) return

    try {
      setSubmitting(true)
      setPhotoValidationError(null) // Clear any previous errors

      const order = await BadgeRequestService.submitBadgeRequest({
        participation_id: participation.id,
        selected_representations: selectedRepresentations,
      })

      // Show success message with payment info if needed
      let successMessage = "Badge request submitted successfully!"
      if (totalAmount > 0) {
        successMessage += ` Payment of ${pricing?.currency} ${totalAmount.toLocaleString()} will be required.`
      }

      toast.success(successMessage)
      setTimeout(() => {
        onOrderCreated(order)
      }, 100)
    } catch (error: any) {
      console.error("Failed to submit order:", error)

      // Handle photo validation error specifically
      if (error.response?.data?.photo_required) {
        setPhotoValidationError(error.response.data)
        setShowPhotoModal(true)
        return
      }

      // Handle other API errors
      if (error.response?.data?.error) {
        toast.error(error.response.data.error)
      } else {
        toast.error("Failed to submit badge request")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleRetryAfterPhotoUpload = () => {
    setShowPhotoModal(false)
    setPhotoValidationError(null)
    // Optionally refresh the data to get updated photo status
    fetchData()
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner color="primary" />
      </div>
    )
  }

  // Filter by name, email, phone, ID doc number, or role — same search
  // pattern used on the Badge Reprints list for consistency. Guarded with
  // fallbacks because some records can have missing phone/email/ID values.
  const matchesSearch = (representation: Representation) => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return true

    const person = representation.person
    const fullName = `${person?.first_name || ""} ${person?.last_name || ""}`
    const haystack = [
      fullName,
      person?.email,
      person?.phone,
      person?.id_doc_number,
      representation.role,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()

    return haystack.includes(term)
  }

  const freeRepresentations = representations.filter((r) => !r.exceeds_limit && matchesSearch(r))
  const paidRepresentations = representations.filter((r) => r.exceeds_limit && matchesSearch(r))
  const totalMatching = freeRepresentations.length + paidRepresentations.length
  const totalAmount = calculateTotalAmount()
  const representativesWithoutPhotos = photoValidationError?.representatives_without_photos || []

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <Button color="secondary" size="sm" onClick={onBack} className="me-3">
                <FaArrowLeft size={12} />
              </Button>
              <div>
                <h5 className="mb-0">Participants - {participation.exhibition_title}</h5>
                <small className="text-muted">Select participants to request badges for</small>
              </div>
            </div>
            <div className="text-end">
              <div className="small text-muted">Max Free: {participation.num_representatives}</div>
              {totalAmount > 0 && (
                <div className="fw-bold text-warning">
                  Payment Required: {pricing?.currency} {totalAmount.toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {representations.length === 0 ? (
            <div className="text-center py-5">
              <h6>No Participants Found</h6>
              <p className="text-muted">No Participants are registered for this participation.</p>
            </div>
          ) : (
            <>
              {/* Search */}
              <Row className="mb-4">
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-semibold">Search Participant</Label>
                    <Input
                      type="text"
                      placeholder="Search by name, email, phone, ID, or role..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={6} className="d-flex align-items-end justify-content-md-end mt-3 mt-md-0">
                  <Badge color="info" pill>
                    {totalMatching} of {representations.length} shown
                  </Badge>
                </Col>
              </Row>

              {totalMatching === 0 && (
                <Alert color="warning" className="text-center">
                  <strong>No participants found matching your search</strong>
                  <div>Try a different name, email, phone, or ID number.</div>
                </Alert>
              )}

              {/* Free Representatives */}
              {freeRepresentations.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-success mb-3">
                    <Badge color="success" className="me-2">
                      FREE
                    </Badge>
                    Within Limit ({freeRepresentations.length} of {participation.num_representatives})
                  </h6>
                  <Row>
                    {freeRepresentations.map((representation) => (
                      <Col md={4} lg={3} key={representation.id} className="mb-3">
                        <Card
                          className={`h-100 ${representation.is_already_ordered ? "border-warning" : ""}`}
                          style={{ fontSize: "0.875rem" }}
                        >
                          <CardBody className="p-3">
                            <FormGroup check className="mb-2">
                              <Input
                                type="checkbox"
                                id={`rep-${representation.id}`}
                                checked={selectedRepresentations.includes(representation.id)}
                                onChange={() => handleRepresentationToggle(representation.id)}
                                disabled={representation.is_already_ordered}
                              />
                              <Label check htmlFor={`rep-${representation.id}`} className="ms-2">
                                <strong style={{ fontSize: "0.8rem" }}>
                                  {representation.person.first_name} {representation.person.last_name}
                                </strong>
                              </Label>
                            </FormGroup>

                            {representation.is_already_ordered && (
                              <Badge color="info" className="mb-2" style={{ fontSize: "0.7rem" }}>
                                Already Ordered
                              </Badge>
                            )}

                            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                              <div className="d-flex align-items-center mb-1">
                                <FaEnvelope className="me-2" size={10} />
                                <span className="text-truncate">{representation.person.email}</span>
                              </div>
                              <div className="d-flex align-items-center mb-1">
                                <FaPhone className="me-2" size={10} />
                                {representation.person.phone}
                              </div>
                              <div className="d-flex align-items-center mb-1">
                                <FaIdCard className="me-2" size={10} />
                                {representation.person.id_doc_number}
                              </div>
                              {representation.role && (
                                <div className="d-flex align-items-center">
                                  <FaUser className="me-2" size={10} />
                                  <span className="text-truncate">{representation.role}</span>
                                </div>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* Paid Representatives */}
              {paidRepresentations.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-warning mb-3">
                    <Badge color="secondary" className="me-2">
                      PAYMENT REQUIRED
                    </Badge>
                    Exceeds Limit ({pricing?.currency} {pricing?.badge_fee.toLocaleString()} each)
                  </h6>
                  <Row>
                    {paidRepresentations.map((representation) => (
                      <Col md={4} lg={3} key={representation.id} className="mb-3">
                        <Card
                          className={`h-100 border-warning ${representation.is_already_ordered ? "border-secondary" : ""}`}
                          style={{ fontSize: "0.875rem" }}
                        >
                          <CardBody className="p-3">
                            <FormGroup check className="mb-2">
                              <Input
                                type="checkbox"
                                id={`rep-${representation.id}`}
                                checked={selectedRepresentations.includes(representation.id)}
                                onChange={() => handleRepresentationToggle(representation.id)}
                                disabled={representation.is_already_ordered}
                              />
                              <Label check htmlFor={`rep-${representation.id}`} className="ms-2">
                                <strong style={{ fontSize: "0.8rem" }}>
                                  {representation.person.first_name} {representation.person.last_name}
                                </strong>
                              </Label>
                            </FormGroup>

                            <div className="d-flex justify-content-between align-items-center mb-2">
                              {representation.is_already_ordered ? (
                                <Badge color="info" style={{ fontSize: "0.7rem" }}>
                                  Already Ordered
                                </Badge>
                              ) : (
                                <Badge color="warning" style={{ fontSize: "0.7rem" }}>
                                  {pricing?.currency} {pricing?.badge_fee.toLocaleString()}
                                </Badge>
                              )}
                            </div>

                            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                              <div className="d-flex align-items-center mb-1">
                                <FaEnvelope className="me-2" size={10} />
                                <span className="text-truncate">{representation.person.email}</span>
                              </div>
                              <div className="d-flex align-items-center mb-1">
                                <FaPhone className="me-2" size={10} />
                                {representation.person.phone}
                              </div>
                              <div className="d-flex align-items-center mb-1">
                                <FaIdCard className="me-2" size={10} />
                                {representation.person.id_doc_number}
                              </div>
                              {representation.role && (
                                <div className="d-flex align-items-center">
                                  <FaUser className="me-2" size={10} />
                                  <span className="text-truncate">{representation.role}</span>
                                </div>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* Payment Info Alert */}
              {totalAmount > 0 && (
                <div className="alert alert-info mb-4">
                  <div className="d-flex align-items-center">
                    <Badge color="warning" className="me-3">
                      PAYMENT NOTICE
                    </Badge>
                    <div>
                      <strong>
                        Payment Required: {pricing?.currency} {totalAmount.toLocaleString()}
                      </strong>
                      <div className="small text-white">
                        You can submit this request now and make payment later from your orders list.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Section */}
              <div className="border-top pt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Selected: {selectedRepresentations.length} representative(s)</strong>
                    {totalAmount > 0 ? (
                      <div className="text-warning">
                        Payment Required: {pricing?.currency} {totalAmount.toLocaleString()}
                      </div>
                    ) : (
                      <div className="text-success">No payment required</div>
                    )}
                  </div>
                  <Button
                    color="primary"
                    size="lg"
                    onClick={handleSubmitOrder}
                    disabled={selectedRepresentations.length === 0 || submitting}
                  >
                    {submitting ? <Spinner size="sm" className="me-2" /> : null}
                    Submit Badge Request
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Photo Validation Error Modal */}
      <Modal isOpen={showPhotoModal} toggle={() => setShowPhotoModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setShowPhotoModal(false)} className="bg-warning opacity-70 text-white">
          <FaCamera className="me-2" />
          Profile Photos Required
        </ModalHeader>
        <ModalBody>
          {photoValidationError && (
            <>
              <Alert color="warning opacity-40" className="mb-4">
                <FaExclamationTriangle className="me-2" />
                <strong>Action Required:</strong> {photoValidationError.message}
              </Alert>

              <div className="mb-4">
                <h6 className="mb-3">
                  <FaUser className="me-2" />
                  Representatives Missing Profile Photos ({representativesWithoutPhotos.length})
                </h6>

                <Card>
                  <CardBody className="p-0">
                    <Table striped className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Name</th>
                          <th>Contact</th>
                          <th>Photo Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {representativesWithoutPhotos.map((rep) => (
                          <tr key={rep.id}>
                            <td>
                              <strong>{rep.name}</strong>
                            </td>
                            <td>
                              <div>
                                <small className="text-muted d-block">{rep.email}</small>
                                <small className="text-muted">{rep.phone}</small>
                              </div>
                            </td>
                            <td>
                              <Badge color="danger">
                                <FaTimesCircle className="me-1" />
                                No Photo
                              </Badge>
                            </td>
                            <td>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleEditRepresentative(rep.id)}
                                title="Edit representative to upload photo"
                              >
                                <FaEdit className="me-1" />
                                Upload Photo
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </div>

              <Alert color="info">
                <h6 className="mb-2">
                  <FaCamera className="me-1" />
                  Why are photos required?
                </h6>
                <ul className="mb-0 small">
                  <li>Profile photos are printed on identification badges</li>
                  <li>Photos help with security and identification at the event</li>
                  <li>All representatives must have photos before badge printing</li>
                  <li>You can upload photos in JPG, PNG, or other image formats</li>
                </ul>
              </Alert>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowPhotoModal(false)}>
            Close
          </Button>
          <Button color="primary" onClick={handleRetryAfterPhotoUpload}>
            I've Uploaded Photos - Retry
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default RepresentationsList
